import IconClose from 'components/icons/close';
import IconTilaCheck from 'components/icons/tila-check';
import React, {
  FunctionComponent,
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useState
} from 'react';
import ReactModal from 'react-modal';
import {
  BigCommerceProductVariantCleaned
} from '../../lib/bigcommerce/types';
import './tila-modal.css';
import TilaReceipt from './tila-receipt';

interface simpleMap {
    [name: string]: string
}

export type TilaModalProps = ReactModal.Props & {
  onConfirm?: () => void;
  sku: string;
};

export type TilaMetafieldsObject = simpleMap & {
  'payment-type': string;
  'num-remaining-payments': string;
  'additional-payments-amount': string;
  'annual-percent-rate': string;
  'finance-charge': string;
  'amount-financed': string;
  'total-of-payments': string;
  'total-sales-price': string;
};

const noop = () => {};

export type TilaProduct = Omit<BigCommerceProductVariantCleaned, 'metafields'> & {
  metafields: TilaMetafieldsObject;
  name: string;
};

const TilaModal: FunctionComponent<TilaModalProps> = ({
  onConfirm = noop,
  onRequestClose = noop,
  isOpen,
  sku,
  onAfterClose,
  ...rest
}) => {
  const [checkboxes, setCheckboxes] = useState({
    checkbox1: false,
    checkbox2: false,
    checkbox3: false,
    checkbox4: false,
  });

  const handleConfirm: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    onRequestClose(event);
    onConfirm();
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, checkboxName: string) => {
    setCheckboxes((prevState) => ({
      ...prevState,
      [checkboxName]: e.target.checked,
    }));
  };

  const handleClose = useCallback(
    (event: MouseEvent | KeyboardEvent) => {
      onRequestClose(event);
      document.body.focus()
    },
    [onRequestClose],
  );

  const [product, setProduct] = useState<TilaProduct | undefined>();
  const [productString, setProductString] = useState<string>('');

  useEffect(() => {
    const getProducts = async () => {
      const response = await fetch(`/api/variant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sku
        }),
      });

      const result = await response.json();
      if (!result) return;

      const tilaProduct = mapTilaProduct(result.title, result.variants[0]) || undefined;
      const dispString = generateProductString(tilaProduct);

      setProduct(tilaProduct);
      setProductString(dispString);
    };

    try {
      getProducts();
    } catch (error) {
      console.error(error);
    }
  }, [sku]);

  const allChecked = checkboxes.checkbox1 && checkboxes.checkbox2 && checkboxes.checkbox3 && checkboxes.checkbox4;

  const generateProductString = (product: TilaProduct | undefined): string => {
    if(!product) return '';
    const name = product.name;
    const numRemainingPayments = parseFloat(product.metafields['num-remaining-payments']) + 1;
    const productDetails =  `${name} - ${numRemainingPayments}Pay`;

    return `I want to secure my peace of mind with a ${productDetails} and the convenience of low monthly payments with no credit check. I promise... I'm good for it!`;
  };

  // If user presses 'Esc' key, close the modal
  const escKeyPressed = useCallback((event: any) => {
    if (event.key === 'Escape') {
      onRequestClose(event);
      document.body.focus();
    }
  },[onRequestClose]);

  useEffect(() => {
    document.addEventListener('keydown', escKeyPressed, false);
    return () => document.removeEventListener('keydown', escKeyPressed, false);
  },[escKeyPressed]);

  return (
    <ReactModal
      {...rest}
      isOpen={isOpen}
      onAfterClose={onAfterClose}
      ariaHideApp={process.env.NODE_ENV !== 'test'}
      bodyOpenClassName="has-activeModal"
      className={{
        base: 'tila modal optimizedCheckout-contentPrimary',
        afterOpen: 'modal--afterOpen',
        beforeClose: 'modal--beforeClose',
      }}
      closeTimeoutMS={200}
      onRequestClose={onRequestClose}
      overlayClassName={{
        base: 'tila modalOverlay',
        afterOpen: 'modalOverlay--afterOpen',
        beforeClose: 'modalOverlay--beforeClose',
      }}
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
    >
      <header className="tila modal-header">
        <h2 className="modal-header-text">Easy Payment Plan Claim Form</h2>

        <button
          className="modal-close"
          data-test="modal-close-button"
          onClick={handleClose}
        >
          <IconClose />
        </button>
      </header>

      <div className="modal-body" data-test="modal-body">
        <div className="tila-modal-main">
          <div className="tila-modal-main-top">
            <div className="tila-modal-main-top-agreement">
              <div className="tila-modal-main-top-agreement-checkmark">
                <IconTilaCheck />
                <p>Yes!</p>
              </div>
              <div className="tila-modal-main-top-agreement-info">
                {productString}
              </div>
            </div>
            <div className="tila-modal-main-top-agreement-warning">
              Please read the information below and check all boxes to claim your Easy Payment Plan
              now.
            </div>
          </div>

          <div className="tila-modal-main-bottom">
            <div className="tila-modal-main-bottom-part">
              <div className="tila-modal-main-bottom-part-check">
                <h3 className="tila-modal-main-bottom-part-check-heading">
                  <input
                    type="checkbox"
                    id="checkbox1"
                    checked={checkboxes.checkbox1}
                    onChange={(e) => handleCheckboxChange(e, 'checkbox1')}
                  />
                  <label htmlFor="checkbox1">Receipt of <abbr title="Truth in Lending Act">TILA</abbr> Disclosure</label>
                </h3>
              </div>
              <div className="tila-modal-main-bottom-part-info">
                <p>
                  By clicking this box, I acknowledge receipt of and agreement to all purchase and finance terms.
                </p>

               <TilaReceipt product={product} />

              </div>
            </div>

            <div className="tila-modal-main-bottom-part">
              <div className="tila-modal-main-bottom-part-check">
                <h3 className="tila-modal-main-bottom-part-check-heading">
                  <input
                    type="checkbox"
                    id="checkbox2"
                    checked={checkboxes.checkbox2}
                    onChange={(e) => handleCheckboxChange(e, 'checkbox2')}
                  />
                  <label htmlFor="checkbox2">Recurring Payment Authorization</label>
                </h3>
              </div>
              <div className="tila-modal-main-bottom-part-info">
                <p>
                  By clicking this box, I authorize 4Patriots to automatically charge my card for monthly payments until my balance is paid in full, or until I amend this authorization by: Contacting <a href="tel:1-800-798-3351">800-798-3351</a> or <a href="mailto:paymentplans@4patriots.com">paymentplans@4patriots.com</a> to change my billing information, or updating my payment method to another method acceptable to 4Patriots.
                </p>
                <p>
                  If I make an off-schedule or partial payment, my remaining automated payments may be adjusted to reflect my new balance. I will receive a notification via email or SMS if my payment schedule is modified.
                </p>
              </div>
            </div>

            <div className="tila-modal-main-bottom-part">
              <div className="tila-modal-main-bottom-part-check">
                <h3 className="tila-modal-main-bottom-part-check-heading">
                  <input
                    type="checkbox"
                    id="checkbox3"
                    checked={checkboxes.checkbox3}
                    onChange={(e) => handleCheckboxChange(e, 'checkbox3')}
                  />
                  <label htmlFor="checkbox3">Permission to Contact</label>
                </h3>
              </div>
              <div className="tila-modal-main-bottom-part-info">
                <p>
                  I agree to allow 4Patriots and its designated agents, vendors, and service providers to contact me at the phone number I provided (or any updated number I provide later) using: Automatic dialing systems, artificial voice, text messages, or pre-recorded messages for informational, telemarketing, and debt-collection purposes. I acknowledge that providing my phone number is not a condition of purchasing any goods or services. I certify that I own the rights to the phone number I have provided.
                </p>
                <h4 className="tila-modal-main-bottom-part-info-heading">Opt-Out Option:</h4>
                <p>
                  I may opt out of telemarketing and informational messages at any time by replying <span className="uppercase">Stop</span> to any message or by contacting <a href="tel:1-800-798-3351">800-798-3351</a> or <a href="mailto:paymentplans@4patriots.com">paymentplans@4patriots.com</a>. Debt collection-related communications will comply with the Fair Debt Collection Practices Act (FDCPA) and applicable laws.
                </p>
              </div>
            </div>

            <div className="tila-modal-main-bottom-part">
              <div className="tila-modal-main-bottom-part-check">
                <h3 className="tila-modal-main-bottom-part-check-heading">
                  <input
                    type="checkbox"
                    id="checkbox4"
                    checked={checkboxes.checkbox4}
                    onChange={(e) => handleCheckboxChange(e, 'checkbox4')}
                  />
                  <label htmlFor="checkbox4">Final Acknowledgment &amp; Agreement</label>
                </h3>
              </div>
              <div className="tila-modal-main-bottom-part-info">
                <p>
                  By clicking this box, I confirm that I have read, understood, and agree to the terms outlined above, including my payment schedule, prepayment rights, recurring payment authorization, and communication preferences.
                </p>
              </div>
            </div>
            
          </div>
        </div>
      </div>

      <footer className="tila-modal-footer">
        <button
          type="button"
          disabled={!allChecked}
          className="cta-button"
          onClick={handleConfirm}
        >
          <span className="cta-button-text">Click to Confirm</span>
        </button>

        <button 
          className="tila-modal-footer-back-button"
          onClick={handleClose}
        >
          No thanks, take me back to the offer.
        </button>
      </footer>
    </ReactModal>
  );
};

export default TilaModal;

const mapTilaProduct = (name: string, variant: BigCommerceProductVariantCleaned): TilaProduct | null => {
    let variantsWithTilaMetafields: TilaProduct | null = null;
    
    const metafieldsObject: TilaMetafieldsObject = {
    'payment-type': '',
    'num-remaining-payments': '',
    'additional-payments-amount': '',
    'annual-percent-rate': '',
    'finance-charge': '',
    'amount-financed': '',
    'total-of-payments': '',
    'total-sales-price': '',
    };

    variant.tilaMetafields.forEach((metafield: any) => {
        if (Object.hasOwn(metafieldsObject, metafield.key)) {
            metafieldsObject[metafield.key] = metafield.value;
        }
    });

    variant.paymentMetafields.forEach((metafield: any) => {
        if (Object.hasOwn(metafieldsObject, metafield.key)) {
            metafieldsObject[metafield.key] = metafield.value;
        }
    });

    const hasTilaMetafield = Object.values(metafieldsObject).some((value) => value !== '');
    const isPaymentPlan = metafieldsObject['payment-type'] === 'payment-plan';

    if (hasTilaMetafield && isPaymentPlan) {
        variantsWithTilaMetafields = {
            ...variant,
            metafields: metafieldsObject,
            name: name,
        };
    }     

    return variantsWithTilaMetafields;
};

